import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, CheckCircle, ArrowRight, Shield, Scale, Upload, Search, Zap, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import FileUpload from '@/components/FileUpload';
import Header from '@/components/Header';
import StickyHeaderB from '@/components/StickyHeaderB';
import HeroSectionB from '@/components/HeroSectionB';
import HeroSection from '@/components/landing/HeroSection';
import PreuveTarif from '@/components/landing/PreuveTarif';
import ExempleAnalyseModal from '@/components/landing/ExempleAnalyseModal';
import DepotForm from '@/components/landing/DepotForm';
import SiteFooter from '@/components/SiteFooter';
import ChatBotVariantC from '@/components/ChatBotVariantC';
import { dossierAPI, partialSubmissionAPI } from '@/utils/api';
import { toast } from 'sonner';
import ReCAPTCHA from 'react-google-recaptcha';
import { trackFileAdded, trackFormStarted, trackFormSubmitted, trackHeroCTAClick } from '@/utils/analytics';
import { useABTest, EXPERIMENTS } from '@/hooks/useABTest';

// Generate unique session ID for partial form tracking
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('form_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('form_session_id', sessionId);
  }
  return sessionId;
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: ''
  });
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [formStarted, setFormStarted] = useState(false);
  const [consentLawyerShare, setConsentLawyerShare] = useState(false);
  // L'exemple de rapport pesait 3 écrans en plein milieu de la page : il passe
  // en modale, ouverte à la demande depuis le hero.
  const [exempleOuvert, setExempleOuvert] = useState(false);
  const recaptchaRef = useRef(null);
  const uploadSectionRef = useRef(null);

  // A/B Test: Get assigned variant
  const { variant, isLoading: isABLoading } = useABTest(EXPERIMENTS.LANDING_PAGE_HERO);
  const isVariantB = variant === 'B';
  const isVariantC = variant === 'C';

  // State for variant C chatbot completion
  const [chatbotCompleted, setChatbotCompleted] = useState(false);
  const [chatbotAnswers, setChatbotAnswers] = useState({});

  // Track form started on first input
  const handleFormStart = () => {
    if (!formStarted) {
      trackFormStarted();
      setFormStarted(true);
    }
  };

  // Save partial form data on blur (for qualitative interviews)
  const handleFieldBlur = async (fieldName, value) => {
    if (!value || !value.trim()) return;

    try {
      const sessionId = getSessionId();
      await partialSubmissionAPI.save({
        session_id: sessionId,
        [fieldName]: value
      });
    } catch (error) {
      // Silent fail - don't disrupt user experience
      console.debug('Partial submission save error:', error);
    }
  };

  // Track when files are added and upload them to partial submissions
  const handleFilesChange = async (newFiles) => {
    const previousCount = files.length;
    setFiles(newFiles);

    if (newFiles.length > 0) {
      trackFileAdded(newFiles.length);
    }

    // Upload new files to partial submissions (for abandoned form recovery)
    const addedFiles = newFiles.slice(previousCount);
    if (addedFiles.length > 0) {
      try {
        const sessionId = getSessionId();
        await partialSubmissionAPI.uploadFiles(sessionId, addedFiles);
        console.debug('Partial files uploaded:', addedFiles.length);
      } catch (error) {
        // Silent fail - don't disrupt user experience
        console.debug('Partial file upload error:', error);
      }
    }
  };

  const handleScrollToUpload = (ctaText = 'CTA Hero') => {
    trackHeroCTAClick(ctaText);
    uploadSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Handlers for Variant C chatbot
  const handleChatbotComplete = (answers) => {
    setChatbotAnswers(answers);
    setChatbotCompleted(true);
    // Scroll to upload form after chatbot completion
    setTimeout(() => {
      uploadSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  const handleChatbotStop = () => {
    // User chose to stop - could redirect or show a message
    console.log('User stopped chatbot');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (files.length === 0) {
      toast.error('Veuillez ajouter au moins un document');
      return;
    }

    if (process.env.REACT_APP_RECAPTCHA_SITE_KEY && !recaptchaToken) {
      toast.error('Veuillez valider le CAPTCHA');
      return;
    }

    if (!consentLawyerShare) {
      toast.error('Veuillez accepter le partage avec les avocats partenaires pour continuer');
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('nom', formData.nom);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('telephone', formData.telephone);
      formDataToSend.append('recaptcha_token', recaptchaToken || 'dev-mode');
      formDataToSend.append('consent_lawyer_share', 'true');
      // Variante A/B persistée en base pour mesurer la conversion sans dépendre du GA4 client-side
      formDataToSend.append('variant', variant || '');
      formDataToSend.append('experiment_id', EXPERIMENTS.LANDING_PAGE_HERO.id);
      files.forEach((file) => {
        formDataToSend.append('files', file);
      });

      const response = await dossierAPI.upload(formDataToSend);

      // Si le backend a détecté un dossier déjà soumis récemment, on le signale clairement
      // (les fichiers ont été ajoutés à l'existant plutôt que de créer un doublon)
      if (response.data.merged_into_existing) {
        toast.info(
          `Vous aviez déjà soumis un dossier sous cet email. Vos ${response.data.added_files_count} nouveau(x) document(s) ont été ajoutés à ce dossier (${response.data.total_files_count} pièces au total). Une seule analyse va être produite.`,
          { duration: 8000 }
        );
      } else {
        toast.success('Dossier soumis avec succès !');
      }

      // Track form submission
      trackFormSubmitted(files.length, !!process.env.REACT_APP_RECAPTCHA_SITE_KEY);

      // Mark partial submission as converted
      try {
        const sessionId = getSessionId();
        await partialSubmissionAPI.markConverted(sessionId);
      } catch (e) {
        // Silent fail
      }

      // Se baser sur la réponse du backend pour déterminer si l'utilisateur est authentifié
      // Si temp_token est présent → utilisateur anonyme
      // Si temp_token est null/undefined → utilisateur authentifié
      if (response.data.temp_token) {
        // Utilisateur anonyme : stocker le temp_token et rediriger avec
        sessionStorage.setItem(`temp_token_${response.data.dossier_id}`, response.data.temp_token);
        setTimeout(() => {
          navigate(`/score/${response.data.dossier_id}?temp_token=${response.data.temp_token}`);
        }, 1500);
      } else {
        // Utilisateur connecté : rediriger sans temp_token
        setTimeout(() => {
          navigate(`/score/${response.data.dossier_id}`);
        }, 1500);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la soumission du dossier');
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
    } finally {
      setLoading(false);
    }
  };

  // Show loading state briefly while determining variant
  if (isABLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f8fafc' }}>
        <div className="loader"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f8fafc' }}>
      {/* Header: Variant A uses full Header, Variant B uses simplified StickyHeaderB, Variant C has no header during chatbot */}
      {isVariantC && !chatbotCompleted ? null : isVariantB ? (
        <StickyHeaderB onCTAClick={handleScrollToUpload} />
      ) : (
        <Header />
      )}

      {/* Hero Section: Different for each variant */}
      {isVariantC && !chatbotCompleted ? (
        <ChatBotVariantC onComplete={handleChatbotComplete} onStop={handleChatbotStop} />
      ) : isVariantB ? (
        <HeroSectionB onCTAClick={handleScrollToUpload} />
      ) : (
        <HeroSection
          onCTAClick={() => handleScrollToUpload('Analyser mes documents — gratuit')}
          onVoirExemple={() => setExempleOuvert(true)}
        />
      )}

      {/* Upload Section - Always visible, but only after chatbot completion for Variant C */}
      {!(isVariantC && !chatbotCompleted) && (
      <section ref={uploadSectionRef} className="py-14 sm:py-20" style={{ backgroundColor: '#f9fafb' }}>
        <DepotForm
          files={files}
          onFilesChange={handleFilesChange}
          formData={formData}
          setFormData={setFormData}
          consentLawyerShare={consentLawyerShare}
          setConsentLawyerShare={setConsentLawyerShare}
          recaptchaRef={recaptchaRef}
          setRecaptchaToken={setRecaptchaToken}
          loading={loading}
          onSubmit={handleSubmit}
          onFormStart={handleFormStart}
          onFieldBlur={handleFieldBlur}
        />
      </section>
      )}

      {!isVariantB && !(isVariantC && !chatbotCompleted) && (
        <PreuveTarif onCTAClick={() => handleScrollToUpload('Tarif — analyser mes documents')} />
      )}


      <SiteFooter />

      <ExempleAnalyseModal
        open={exempleOuvert}
        onOpenChange={setExempleOuvert}
        onCTAClick={() => handleScrollToUpload('Exemple — analyser mes documents')}
      />
    </div>
  );
};

export default LandingPage;